import React from 'react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from 'react-router-dom';
import { FileIcon, InfoIcon } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import HeaderWithBackButton from '@/components/common/HeaderWithBackButton';

const StorageDocumentation: React.FC = () => {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-1 container max-w-7xl mx-auto px-4 py-8 mt-20">
        <HeaderWithBackButton 
          title="Storage System Documentation"
          subtitle="Comprehensive guide to the file storage system architecture, modules, and usage examples."
          backTo="/"
          backLabel="Back to Home"
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Navigation</CardTitle>
              </CardHeader>
              <CardContent>
                <nav className="space-y-1">
                  <a href="#overview" className="block p-2 hover:bg-muted rounded-md">Overview</a>
                  <a href="#modules" className="block p-2 hover:bg-muted rounded-md">Modules</a>
                  <a href="#examples" className="block p-2 hover:bg-muted rounded-md">Usage Examples</a>
                  <a href="#validation" className="block p-2 hover:bg-muted rounded-md">Validation</a>
                  <a href="#security" className="block p-2 hover:bg-muted rounded-md">Security</a>
                  <a href="#troubleshooting" className="block p-2 hover:bg-muted rounded-md">Troubleshooting</a>
                </nav>
                
                <Separator className="my-4" />
                
                <div className="space-y-2">
                  <Link to="/dev/storage-diagnostics">
                    <Button variant="outline" size="sm" className="w-full">
                      Run Diagnostics
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="lg:col-span-3 space-y-8">
            <Card id="overview">
              <CardHeader>
                <CardTitle>System Overview</CardTitle>
                <CardDescription>
                  Architecture of the paper storage system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  The paper storage system is built on top of Supabase Storage and follows a modular architecture 
                  for better maintainability, testing, and extension. It provides secure file uploads, validation, 
                  and management capabilities for PDF documents.
                </p>
                
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Note</AlertTitle>
                  <AlertDescription>
                    This system requires authentication to work properly. All storage operations are secured
                    with Row Level Security (RLS) policies.
                  </AlertDescription>
                </Alert>
                
                <h3 className="text-lg font-medium mt-4">System Diagram</h3>
                <div className="p-4 bg-muted rounded-lg text-sm">
                  <pre className="whitespace-pre-wrap">
{`
┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐
│                 │     │                 │     │                 │
│  File Upload    │────▶│  Validation     │────▶│  Token Service  │
│  Component      │     │  Service        │     │                 │
│                 │     │                 │     │                 │
└─────────────────┘     └─────────────────┘     └─────────────────┘
                                                        │
                                                        ▼
┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐
│                 │     │                 │     │                 │
│  Logging        │◀────│  Upload         │◀────│  Retry Service  │
│  Service        │     │  Service        │     │                 │
│                 │     │                 │     │                 │
└─────────────────┘     └─────────────────┘     └─────────────────┘
                                │
                                ▼
                        ┌─────────────────┐
                        │                 │
                        │  URL Service    │
                        │                 │
                        │                 │
                        └─────────────────┘
`}
                  </pre>
                </div>
              </CardContent>
            </Card>
            
            <Card id="modules">
              <CardHeader>
                <CardTitle>Modules</CardTitle>
                <CardDescription>
                  Detailed explanation of each module in the storage system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="upload">
                  <TabsList className="grid grid-cols-3 lg:grid-cols-6">
                    <TabsTrigger value="upload">Upload</TabsTrigger>
                    <TabsTrigger value="validation">Validation</TabsTrigger>
                    <TabsTrigger value="token">Token</TabsTrigger>
                    <TabsTrigger value="url">URL</TabsTrigger>
                    <TabsTrigger value="retry">Retry</TabsTrigger>
                    <TabsTrigger value="logging">Logging</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="upload" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">Upload Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Handles file uploads to Supabase Storage with security tokens and proper error handling.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">uploadFileToStorage</code> - Uploads a file to Supabase Storage with security headers</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/upload-service.ts</p>
                  </TabsContent>
                  
                  <TabsContent value="validation" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">Validation Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Validates files before upload to ensure they meet size and type requirements, and checks rate limits.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">validatePaperFile</code> - Validates file type, size and extension</li>
                      <li><code className="font-mono bg-muted px-1 rounded">checkUploadRateLimit</code> - Enforces upload rate limiting</li>
                      <li><code className="font-mono bg-muted px-1 rounded">verifyStorageBucket</code> - Ensures storage bucket exists</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/validation.ts</p>
                  </TabsContent>
                  
                  <TabsContent value="token" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">Token Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Generates security tokens for upload authentication.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">getSecurityToken</code> - Gets a security token for upload authentication</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/token-service.ts</p>
                  </TabsContent>
                  
                  <TabsContent value="url" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">URL Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Manages file URLs and path conversions.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">getPublicUrl</code> - Gets a public URL for a file in storage</li>
                      <li><code className="font-mono bg-muted px-1 rounded">getPathFromUrl</code> - Converts a public URL back to a storage path</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/url-service.ts</p>
                  </TabsContent>
                  
                  <TabsContent value="retry" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">Retry Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Implements retry logic for unreliable network operations.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">withRetry</code> - Retries an operation multiple times with delay</li>
                      <li><code className="font-mono bg-muted px-1 rounded">isRetryableError</code> - Checks if an error is likely to be resolved by retrying</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/retry-service.ts</p>
                  </TabsContent>
                  
                  <TabsContent value="logging" className="p-4 border rounded-md mt-4">
                    <h3 className="text-lg font-semibold">Logging Service</h3>
                    <p className="text-muted-foreground mb-4">
                      Handles logging of upload activities for monitoring and security.
                    </p>
                    
                    <h4 className="font-medium mt-4 mb-2">Functions:</h4>
                    <ul className="list-disc list-inside space-y-2">
                      <li><code className="font-mono bg-muted px-1 rounded">logUploadAction</code> - Logs upload activity to the database</li>
                      <li><code className="font-mono bg-muted px-1 rounded">recordUploadAttempt</code> - Records an upload attempt for monitoring</li>
                    </ul>
                    
                    <h4 className="font-medium mt-4 mb-2">File Location:</h4>
                    <p className="text-sm font-mono bg-muted p-2 rounded">src/lib/api/paper-storage/logging-service.ts</p>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
            
            <Card id="examples">
              <CardHeader>
                <CardTitle>Usage Examples</CardTitle>
                <CardDescription>
                  Code examples for common storage operations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Uploading a File</h3>
                    <div className="bg-muted p-4 rounded-md">
                      <pre className="whitespace-pre-wrap text-sm">
{`// Import the main upload function
import { uploadPaperFile } from '@/lib/api/paper-storage';

// In a React component
const uploadFile = async (file: File) => {
  try {
    const fileUrl = await uploadPaperFile(file);
    if (fileUrl) {
      console.log('File uploaded successfully:', fileUrl);
    } else {
      console.error('Upload failed');
    }
  } catch (error) {
    console.error('Error uploading file:', error);
  }
};`}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Validating a File</h3>
                    <div className="bg-muted p-4 rounded-md">
                      <pre className="whitespace-pre-wrap text-sm">
{`// Import the validation function
import { validatePaperFile } from '@/lib/api/paper-storage';

// In a React component
const validateFile = (file: File) => {
  const result = validatePaperFile(file);
  if (result.valid) {
    console.log('File is valid');
  } else {
    console.error('File validation failed:', result.message);
  }
};`}
                      </pre>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Using Retry Logic</h3>
                    <div className="bg-muted p-4 rounded-md">
                      <pre className="whitespace-pre-wrap text-sm">
{`// Import the retry function
import { withRetry } from '@/lib/api/paper-storage';

// Using retry logic for any async operation
const fetchWithRetry = async (url: string) => {
  const result = await withRetry(
    async () => {
      const response = await fetch(url);
      if (!response.ok) return null;
      return await response.json();
    },
    3,  // max retries
    1000 // delay in ms
  );
  
  return result;
};`}
                      </pre>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card id="validation">
              <CardHeader>
                <CardTitle>Validation Rules</CardTitle>
                <CardDescription>
                  Rules for file validation and rate limiting
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <h3 className="text-lg font-semibold">File Validation</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>File must be present (not null)</li>
                  <li>File must be a PDF (MIME type: application/pdf)</li>
                  <li>File extension must be .pdf</li>
                  <li>File size must be under 5MB</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4">Rate Limiting</h3>
                <ul className="list-disc list-inside space-y-2">
                  <li>Maximum 10 uploads per hour per user</li>
                  <li>Maximum 5 failed attempts before cooldown period</li>
                  <li>Cooldown period: 60 seconds</li>
                </ul>
                
                <Alert className="mt-4">
                  <FileIcon className="h-4 w-4" />
                  <AlertTitle>File Size Guidelines</AlertTitle>
                  <AlertDescription>
                    While the system allows files up to 5MB, we recommend keeping files below 2MB
                    for optimal performance and user experience.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
            
            <Card id="security">
              <CardHeader>
                <CardTitle>Security Measures</CardTitle>
                <CardDescription>
                  Security features of the storage system
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="list-disc list-inside space-y-2">
                  <li>Authentication required for all upload operations</li>
                  <li>Row Level Security (RLS) policies restricting access to user's own files</li>
                  <li>Upload tokens for request verification</li>
                  <li>UUID-based file naming to prevent path traversal attacks</li>
                  <li>Content-Type validation to prevent MIME type spoofing</li>
                  <li>Rate limiting to prevent abuse</li>
                  <li>Comprehensive logging for security auditing</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card id="troubleshooting">
              <CardHeader>
                <CardTitle>Troubleshooting</CardTitle>
                <CardDescription>
                  Common issues and their solutions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <h3 className="text-lg font-semibold">Common Errors</h3>
                
                <div className="space-y-4">
                  <div className="p-4 border rounded-md">
                    <h4 className="font-medium text-red-600 dark:text-red-400">Storage configuration error</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      The storage system is not properly configured.
                    </p>
                    <p className="font-medium mt-3 text-sm">Solution:</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Verify the 'papers' bucket exists in Supabase</li>
                      <li>Check that RLS policies are correctly configured</li>
                      <li>Ensure the user is authenticated</li>
                      <li>Run the storage diagnostics tool</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border rounded-md">
                    <h4 className="font-medium text-red-600 dark:text-red-400">Authentication required</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      You must be logged in to upload files.
                    </p>
                    <p className="font-medium mt-3 text-sm">Solution:</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Log in to your account</li>
                      <li>If already logged in, try logging out and back in</li>
                      <li>Clear your browser cache and cookies</li>
                    </ul>
                  </div>
                  
                  <div className="p-4 border rounded-md">
                    <h4 className="font-medium text-red-600 dark:text-red-400">Upload limit reached</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      You can only upload X files per hour.
                    </p>
                    <p className="font-medium mt-3 text-sm">Solution:</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Wait for the rate limit to reset (1 hour from first upload)</li>
                      <li>If this is a persistent issue, contact the administrator</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-semibold mb-3">Diagnostic Tools</h3>
                  <p className="mb-3">
                    Use our built-in diagnostic tool to identify and resolve storage issues:
                  </p>
                  <Link to="/dev/storage-diagnostics">
                    <Button>
                      Run Storage Diagnostics
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default StorageDocumentation;
